// frontend/src/features/livestock/PhotoLightbox.tsx
/**
 * Full-screen image viewer for timeline photos and tank shots.
 *
 * Uses a Mantine Modal with:
 * - Full viewport size
 * - Dark overlay background (the --rdh-lightbox-bg token)
 * - Escape or the close button to close
 * - Image fits within viewport maintaining aspect ratio
 *
 * Zoom (docs/plans/2026-10-29-photo-lightbox-zoom.md), with compare's zoom
 * code: pinch, ctrl+wheel or a trackpad pinch zoom up to LIGHTBOX_ZOOM_MAX;
 * zoomed, a drag, a plain wheel or the arrow keys pan; a double tap or click
 * toggles fit and 2x; + - 0 and the zoom buttons. The view stays on the photo,
 * not its letterbox. A new photo, closing or turning the phone go back to fit.
 *
 * Past 2x, a larger copy of the photo when the caller can load one
 * (docs/plans/2026-10-30-photo-lightbox-large.md): fetched once per photo,
 * decoded off screen, then laid exactly over the 1920 px photo, which stays
 * underneath, so the view doesn't move.
 *
 * Optional, for tank shots: a caption (date and note) and Edit, Delete and
 * cover actions in a bar under the photo, plus any further badges and actions
 * (Line up).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, ReactNode, SyntheticEvent } from 'react';
import type { LargePhoto } from '@/types/tankPhotos';
import {
  Modal,
  Image,
  ActionIcon,
  Box,
  Badge,
  Button,
  Group,
  Stack,
  VisuallyHidden,
} from '@mantine/core';
import { useDebouncedValue, useElementSize, useMediaQuery } from '@mantine/hooks';
import { IconPencil, IconPhotoStar, IconRefresh, IconTrash, IconX } from '@tabler/icons-react';
import {
  NO_ZOOM,
  ZOOM_STEP,
  clampZoom,
  isZoomed,
  panZoom,
  zoomAt,
  zoomLabel,
  zoomTransform,
  type CompareZoom,
  type ZoomContent,
  type ZoomLimits,
} from './compareZoom';
import { useZoomGestures } from './useZoomGestures';
import { ZoomControls } from './ZoomControls';
import classes from './PhotoLightbox.module.css';

export interface PhotoLightboxCover {
  /** This photo is the tank's chosen cover. */
  isCover: boolean;
  onSetCover: () => void;
  /** Back to the automatic cover, the latest shot. */
  onUseLatest: () => void;
  /** A cover change is being sent. */
  pending?: boolean;
}

export interface PhotoLightboxProps {
  /** Signed URL for the full-size photo */
  photoUrl: string | null;
  /** Whether the lightbox is open */
  opened: boolean;
  /** Callback when the lightbox should close */
  onClose: () => void;
  /** Alt text for the photo */
  alt?: string;
  /** Under the photo, e.g. its date and note */
  caption?: ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  cover?: PhotoLightboxCover;
  /**
   * Which photo this is (its id). Zoom goes back to fit when it changes, but
   * not when only the signed URL is renewed. The URL without its query when
   * not given.
   */
  photoKey?: string | number;
  /** More badges at the start of the bar, after Cover (e.g. Lined up). */
  badges?: ReactNode;
  /** More buttons in the bar, before Edit; styled with LIGHTBOX_BAR_BUTTON. */
  actions?: ReactNode;
  /**
   * A larger copy of the photo, fetched once when the zoom first passes
   * LIGHTBOX_LARGE_FROM_ZOOM. Resolves to null when there isn't one. The
   * 1920 px photo stays until the larger one has decoded.
   */
  loadLarge?: () => Promise<LargePhoto | null>;
}

/**
 * Above the floating Log data button (an Affix, z-index 200 like modals), which
 * otherwise sits on the bar's last button on a phone. Dialogs opened from the
 * bar (edit, delete) go above this.
 */
export const LIGHTBOX_Z_INDEX = 250;

/** Bar buttons: light text on the dark viewer, 44 px high for a thumb. */
export const LIGHTBOX_BAR_BUTTON = { variant: 'subtle', color: 'gray.0', h: 44, px: 'sm' } as const;
const BAR_BUTTON = LIGHTBOX_BAR_BUTTON;

/**
 * The viewer shows the 1920 px variant: an iPhone shows it pixel for pixel at
 * about 2x, and much past 3x it's only soft.
 */
export const LIGHTBOX_ZOOM_MAX = 3;
/** Past this zoom the 1920 px photo is soft on a phone, so a larger copy is loaded. */
export const LIGHTBOX_LARGE_FROM_ZOOM = 2;
/** A double tap at fit zooms to this. */
const DOUBLE_TAP_ZOOM = 2;
/** An arrow key pans by this much of the view. */
const KEY_PAN = 0.1;
/** Room around the photo at fit; none on a short screen (a phone on its side). */
const PHOTO_PADDING_PX = 24;
const SHORT_SCREEN = '(max-height: 500px)';

/** Where a photo of `natural` size sits in an area (fractions), as `scale-down` places it. */
export function photoBox(
  area: { width: number; height: number },
  natural: { width: number; height: number },
  padding: number
): ZoomContent | undefined {
  const innerWidth = area.width - 2 * padding;
  const innerHeight = area.height - 2 * padding;
  if (innerWidth <= 0 || innerHeight <= 0 || natural.width <= 0 || natural.height <= 0) {
    return undefined;
  }
  const fit = Math.min(1, innerWidth / natural.width, innerHeight / natural.height);
  const left = (area.width - natural.width * fit) / 2 / area.width;
  const top = (area.height - natural.height * fit) / 2 / area.height;
  return { left, top, right: 1 - left, bottom: 1 - top };
}

/** The browser's data saver is on (Chrome on Android; iOS Safari doesn't say). */
function savingData(): boolean {
  const { connection } = navigator as Navigator & { connection?: { saveData?: boolean } };
  return connection?.saveData === true;
}

/** Loads and decodes an image off screen, so showing it doesn't stall the zoom. */
async function preloadImage(url: string): Promise<void> {
  const img = new window.Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = (): void => resolve();
    img.onerror = (): void => reject(new Error('The larger photo did not load'));
    img.src = url;
  });
  // Best effort: Safari can reject decode() for an image that loaded fine
  await img.decode?.().catch(() => undefined);
}

/** Keys typed into a field aren't zoom keys. */
function isTyping(target: EventTarget): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

export function PhotoLightbox({
  photoUrl,
  opened,
  onClose,
  alt = 'Full-size photo',
  caption,
  onEdit,
  onDelete,
  cover,
  photoKey,
  badges,
  actions,
  loadLarge,
}: PhotoLightboxProps): JSX.Element {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      padding={0}
      withCloseButton={false}
      zIndex={LIGHTBOX_Z_INDEX}
      overlayProps={{ backgroundOpacity: 0.9, color: 'var(--mantine-color-black)', blur: 0 }}
      styles={{
        body: {
          height: '100%',
        },
        content: {
          backgroundColor: 'var(--rdh-lightbox-bg)',
        },
        header: {
          display: 'none',
        },
      }}
    >
      {/* Mounted only while open, so closing drops the zoom */}
      <LightboxBody
        photoUrl={photoUrl}
        onClose={onClose}
        alt={alt}
        caption={caption}
        onEdit={onEdit}
        onDelete={onDelete}
        cover={cover}
        photoKey={photoKey ?? photoUrl?.split('?')[0]}
        badges={badges}
        actions={actions}
        loadLarge={loadLarge}
      />
    </Modal>
  );
}

type LightboxBodyProps = Omit<PhotoLightboxProps, 'opened' | 'alt'> & { alt: string };

function LightboxBody({
  photoUrl,
  onClose,
  alt,
  caption,
  onEdit,
  onDelete,
  cover,
  photoKey,
  badges,
  actions,
  loadLarge,
}: LightboxBodyProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const { ref: areaRef, width, height } = useElementSize();
  const [zoom, setZoom] = useState<CompareZoom>(NO_ZOOM);
  // A step from a button, key or double tap eases; a gesture follows the fingers
  const [animate, setAnimate] = useState(false);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const shortScreen = useMediaQuery(SHORT_SCREEN);
  const portrait = useMediaQuery('(orientation: portrait)');
  const padding = shortScreen ? 0 : PHOTO_PADDING_PX;
  const [spoken] = useDebouncedValue(zoomLabel(zoom), 400);

  const limits = useMemo<ZoomLimits>(
    () => ({
      max: LIGHTBOX_ZOOM_MAX,
      content: natural ? photoBox({ width, height }, natural, padding) : undefined,
    }),
    [width, height, natural, padding]
  );

  const changeZoom = (next: CompareZoom, eased: boolean): void => {
    setAnimate(eased);
    setZoom(next);
  };

  // Another photo, or the phone turned: back to fit
  useEffect(() => {
    setAnimate(false);
    setZoom(NO_ZOOM);
  }, [photoKey, portrait]);

  // The larger copy, once decoded, and for which photo it was asked
  const [large, setLarge] = useState<string | null>(null);
  const largeFor = useRef<string | number | undefined>(undefined);
  const loadLargeRef = useRef(loadLarge);
  loadLargeRef.current = loadLarge;
  const photoKeyRef = useRef(photoKey);
  photoKeyRef.current = photoKey;

  // Another photo: drop the larger copy of the last one
  useEffect(() => {
    setLarge(null);
    largeFor.current = undefined;
  }, [photoKey]);

  // Past 2x, once per photo: fetch the larger copy and show it once decoded
  const pastLargeZoom = zoom.scale > LIGHTBOX_LARGE_FROM_ZOOM;
  const zoomedPhoto = useRef(photoKey);
  useEffect(() => {
    // A new photo renders once with the last one's zoom, before it goes back to fit
    if (zoomedPhoto.current !== photoKey) {
      zoomedPhoto.current = photoKey;
      return;
    }
    const load = loadLargeRef.current;
    if (!pastLargeZoom || !load || photoKey === undefined || largeFor.current === photoKey) {
      return;
    }
    if (savingData()) return;
    const key = photoKey;
    largeFor.current = key;
    load()
      .then(async (result) => {
        if (!result) return;
        await preloadImage(result.url);
        if (photoKeyRef.current === key) setLarge(result.url);
      })
      .catch((error: unknown) => {
        // The 1920 px photo stays; no retry while the viewer is open
        console.warn('PhotoLightbox: no larger photo', error);
      });
  }, [pastLargeZoom, photoKey]);

  // The area resized or the photo loaded: keep the view on the photo
  useEffect(() => {
    setZoom((current) => clampZoom(current, limits));
  }, [limits]);

  // Safari's own pinch zoom, which a pinch starting on the bar would otherwise do
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const stop = (event: Event): void => event.preventDefault();
    root.addEventListener('gesturestart', stop);
    root.addEventListener('gesturechange', stop);
    return () => {
      root.removeEventListener('gesturestart', stop);
      root.removeEventListener('gesturechange', stop);
    };
  }, []);

  const gestures = useZoomGestures({
    ref: areaRef,
    zoom,
    onZoomChange: (next) => changeZoom(next, false),
    panWithOnePointer: () => true,
    preventBrowserZoom: true,
    limits,
    wheelPans: true,
    onDoubleTap: (event: PointerEvent<HTMLElement>) => {
      if (isZoomed(zoom)) {
        changeZoom(NO_ZOOM, true);
        return;
      }
      const box = areaRef.current?.getBoundingClientRect();
      const u = box && box.width > 0 ? (event.clientX - box.left) / box.width : 0.5;
      const v = box && box.height > 0 ? (event.clientY - box.top) / box.height : 0.5;
      changeZoom(zoomAt(zoom, DOUBLE_TAP_ZOOM, u, v, limits), true);
    },
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.altKey || event.ctrlKey || event.metaKey || isTyping(event.target)) return;
    const pan: Partial<Record<string, [number, number]>> = {
      ArrowLeft: [KEY_PAN, 0],
      ArrowRight: [-KEY_PAN, 0],
      ArrowUp: [0, KEY_PAN],
      ArrowDown: [0, -KEY_PAN],
    };
    const step = pan[event.key];
    if (event.key === '+' || event.key === '=') {
      changeZoom(zoomAt(zoom, ZOOM_STEP, 0.5, 0.5, limits), true);
    } else if (event.key === '-' || event.key === '_') {
      changeZoom(zoomAt(zoom, 1 / ZOOM_STEP, 0.5, 0.5, limits), true);
    } else if (event.key === '0') {
      changeZoom(NO_ZOOM, true);
    } else if (step && isZoomed(zoom)) {
      changeZoom(panZoom(zoom, step[0], step[1], limits), true);
    } else {
      return;
    }
    event.preventDefault();
  };

  const onPhotoLoad = (event: SyntheticEvent<HTMLImageElement>): void => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setNatural({ width: naturalWidth, height: naturalHeight });
    }
  };

  const hasActions = !!onEdit || !!onDelete || !!cover || !!badges || !!actions;

  return (
    <Box ref={rootRef} className={classes.root} onKeyDown={onKeyDown}>
      {/* Close button - positioned top-right */}
      <ActionIcon
        variant="subtle"
        color="gray.0"
        size="xl"
        radius="xl"
        onClick={onClose}
        aria-label="Close lightbox"
        className={classes.close}
      >
        <IconX size={24} />
      </ActionIcon>

      {/* Photo */}
      <Box
        ref={areaRef}
        className={classes.area}
        data-zoomed={isZoomed(zoom) || undefined}
        data-testid="lightbox-zoom-area"
        onPointerDown={(event) => gestures.pointerDown(event)}
        onPointerMove={(event) => gestures.pointerMove(event)}
        onPointerUp={(event) => gestures.pointerEnd(event, true)}
        onPointerCancel={(event) => gestures.pointerEnd(event, false)}
        onLostPointerCapture={(event) => {
          if (event.target === event.currentTarget) gestures.forget(event.pointerId);
        }}
        onPointerLeave={(event) => {
          if (!areaRef.current?.hasPointerCapture?.(event.pointerId)) {
            gestures.forget(event.pointerId);
          }
        }}
        onDragStart={(event) => event.preventDefault()}
      >
        <Box
          className={classes.layer}
          p={padding}
          style={zoomTransform(zoom)}
          data-animate={animate || undefined}
          data-testid="lightbox-zoom-layer"
        >
          {photoUrl && (
            <Image
              src={photoUrl}
              alt={alt}
              fit="scale-down"
              w="100%"
              h="100%"
              draggable={false}
              className={classes.photo}
              onLoad={onPhotoLoad}
            />
          )}
          {/* Placed by the 1920 px photo's box, so the swap can't move the view */}
          {photoUrl && large && limits.content && (
            <Image
              src={large}
              alt=""
              aria-hidden
              fit="fill"
              draggable={false}
              className={classes.large}
              data-testid="lightbox-large"
              style={{
                left: `${limits.content.left * 100}%`,
                top: `${limits.content.top * 100}%`,
                width: `${(limits.content.right - limits.content.left) * 100}%`,
                height: `${(limits.content.bottom - limits.content.top) * 100}%`,
              }}
            />
          )}
        </Box>
      </Box>

      <Box className={classes.bar}>
        <Stack gap="sm" maw={960} mx="auto">
          <Group
            justify={caption ? 'space-between' : 'center'}
            align="center"
            gap="sm"
            wrap="nowrap"
          >
            {caption && (
              <Box c="gray.0" className={classes.caption}>
                {caption}
              </Box>
            )}
            <ZoomControls zoom={zoom} onZoomChange={changeZoom} dark limits={limits} />
          </Group>
          {hasActions && (
            <Group gap={4} wrap="wrap">
              {cover?.isCover && (
                <Badge
                  variant="filled"
                  color="teal"
                  leftSection={<IconPhotoStar size={12} />}
                  mr="xs"
                >
                  Cover
                </Badge>
              )}
              {badges}
              {cover &&
                (cover.isCover ? (
                  <Button
                    {...BAR_BUTTON}
                    leftSection={<IconRefresh size={16} />}
                    onClick={cover.onUseLatest}
                    loading={cover.pending}
                  >
                    Use latest shot
                  </Button>
                ) : (
                  <Button
                    {...BAR_BUTTON}
                    leftSection={<IconPhotoStar size={16} />}
                    onClick={cover.onSetCover}
                    loading={cover.pending}
                  >
                    Set as cover
                  </Button>
                ))}
              {actions}
              {onEdit && (
                <Button {...BAR_BUTTON} leftSection={<IconPencil size={16} />} onClick={onEdit}>
                  Edit
                </Button>
              )}
              {onDelete && (
                <Button {...BAR_BUTTON} leftSection={<IconTrash size={16} />} onClick={onDelete}>
                  Delete
                </Button>
              )}
            </Group>
          )}
        </Stack>
      </Box>

      <VisuallyHidden role="status" aria-live="polite">
        {`Zoom ${spoken}`}
      </VisuallyHidden>
    </Box>
  );
}
